import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Separator } from "@/components/ui/separator";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  useLifestyleTypes,
  useLifestyleFrequencies,
  useCreatePatientLifestyle,
  useUpdatePatientLifestyle,
  useDeletePatientLifestyle,
} from "@/hooks/useConsultation";
import { useToast } from "@/hooks/use-toast";
import type {
  PatientLifestyle,
  CreatePatientLifestyleRequest,
  UpdatePatientLifestyleRequest,
} from "@/types/appointments";
import { Search, Plus, X, ArrowLeft, Coffee } from "lucide-react";

const lifestyleSchema = z.object({
  lifestyle_type: z.number().min(1, "Please select a lifestyle type"),
  lifestyle_frequency: z.number().min(1, "Please select a frequency"),
});

type LifestyleFormData = z.infer<typeof lifestyleSchema>;

interface AddLifestyleDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  patientId: string;
  appointmentId: string;
  existingEntry?: PatientLifestyle;
  patientLifestyle?: PatientLifestyle[];
}

export const AddLifestyleDialog = ({
  open,
  onOpenChange,
  patientId,
  appointmentId,
  existingEntry,
  patientLifestyle = [],
}: AddLifestyleDialogProps) => {
  const { toast } = useToast();
  const [view, setView] = useState<"manage" | "form">("manage");
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedType, setSelectedType] = useState<number | null>(null);

  const { data: lifestyleTypes = [] } = useLifestyleTypes();
  const { data: lifestyleFrequencies = [] } = useLifestyleFrequencies();

  const createMutation = useCreatePatientLifestyle();
  const updateMutation = useUpdatePatientLifestyle();
  const deleteMutation = useDeletePatientLifestyle();

  const isEditing = !!existingEntry;
  const isLoading =
    createMutation.isPending ||
    updateMutation.isPending ||
    deleteMutation.isPending;

  // Reset view when dialog opens/closes
  useEffect(() => {
    if (open) {
      if (existingEntry) {
        setView("form");
        setSelectedType(existingEntry.lifestyle_type);
      } else {
        setView("manage");
        setSearchTerm("");
        setSelectedType(null);
      }
    }
  }, [open, existingEntry]);

  const form = useForm<LifestyleFormData>({
    resolver: zodResolver(lifestyleSchema),
    defaultValues: {
      lifestyle_type: existingEntry?.lifestyle_type || 0,
      lifestyle_frequency: existingEntry?.lifestyle_frequency || 0,
    },
  });

  useEffect(() => {
    if (selectedType && view === "form") {
      form.setValue("lifestyle_type", selectedType);
    }
    if (existingEntry && view === "form") {
      form.reset({
        lifestyle_type: existingEntry.lifestyle_type,
        lifestyle_frequency: existingEntry.lifestyle_frequency,
      });
    } else if (!existingEntry && view === "form" && selectedType) {
      form.reset({
        lifestyle_type: selectedType,
        lifestyle_frequency: 0,
      });
    }
  }, [selectedType, view, existingEntry, form]);

  const handleAddClick = (typeId: number) => {
    setSelectedType(typeId);
    setView("form");
  };

  const handleRemoveClick = async (lifestyle: PatientLifestyle) => {
    try {
      await deleteMutation.mutateAsync({
        patientId: appointmentId,
        lifestyleId: lifestyle.id,
      });
      toast({
        title: "Success",
        description: "Lifestyle removed successfully",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to remove lifestyle",
        variant: "destructive",
      });
    }
  };

  const onSubmit = async (data: LifestyleFormData) => {
    try {
      const requestData:
        | CreatePatientLifestyleRequest
        | UpdatePatientLifestyleRequest = {
        lifestyle_type: data.lifestyle_type,
        lifestyle_frequency: data.lifestyle_frequency,
      };

      if (isEditing) {
        await updateMutation.mutateAsync({
          patientId: appointmentId,
          lifestyleId: existingEntry.id,
          data: requestData as UpdatePatientLifestyleRequest,
        });
        toast({
          title: "Success",
          description: "Lifestyle updated successfully",
        });
        onOpenChange(false);
      } else {
        await createMutation.mutateAsync({
          patientId: appointmentId,
          data: requestData as CreatePatientLifestyleRequest,
        });
        toast({
          title: "Success",
          description: "Lifestyle added successfully",
        });
        setView("manage");
        setSelectedType(null);
      }

      form.reset();
    } catch (error) {
      toast({
        title: "Error",
        description: isEditing
          ? "Failed to update lifestyle"
          : "Failed to add lifestyle",
        variant: "destructive",
      });
    }
  };

  const addedTypeIds = new Set(patientLifestyle.map((l) => l.lifestyle_type));

  const availableTypes = lifestyleTypes.filter((type) => {
    const matchesSearch = type.name
      .toLowerCase()
      .includes(searchTerm.toLowerCase());
    const notAdded = !addedTypeIds.has(type.id);
    return matchesSearch && notAdded;
  });

  const getTypeName = (id: number) =>
    lifestyleTypes.find((t) => t.id === id)?.name || `Type ${id}`;
  const getFrequencyName = (id: number) =>
    lifestyleFrequencies.find((f) => f.id === id)?.frequency || "";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px] h-[600px] flex flex-col p-0 gap-0 overflow-hidden border-border/60">
        <DialogHeader className="p-6 pb-0 shrink-0">
          <DialogTitle className="z-h2 text-foreground flex items-center gap-2">
            {view === "form" && !isEditing && (
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 -ml-2 mr-1"
                onClick={() => setView("manage")}
              >
                <ArrowLeft className="h-4 w-4" />
              </Button>
            )}
            {view === "form"
              ? isEditing
                ? "Edit Lifestyle"
                : "Add Details"
              : "Add Lifestyle"}
          </DialogTitle>
        </DialogHeader>
        <Separator className="mt-3" />

        {view === "manage" ? (
          <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
            {/* Already Added Section */}
            <div className="px-6 pb-4 border-b border-border/60 shrink-0">
              <h3 className="z-eyebrow text-muted-foreground mb-3">
                Already Added
              </h3>
              {patientLifestyle.length > 0 ? (
                <div className="grid grid-cols-2 gap-2.5 max-h-[160px] overflow-y-auto">
                  {patientLifestyle.map((lifestyle) => (
                    <div
                      key={lifestyle.id}
                      className="bg-card p-3 rounded-xl border border-border shadow-sm relative group"
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <p className="z-body-md text-foreground">
                            {getTypeName(lifestyle.lifestyle_type)}
                          </p>
                          <p className="mt-1 z-caption text-muted-foreground">
                            {getFrequencyName(lifestyle.lifestyle_frequency)}
                          </p>
                        </div>
                        <button
                          onClick={() => handleRemoveClick(lifestyle)}
                          className="shrink-0 rounded-full p-0.5 transition-colors text-muted-foreground/60 hover:bg-destructive/10 hover:text-destructive"
                        >
                          <X className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="z-secondary text-muted-foreground/60 italic py-2">
                  No lifestyle habits added yet
                </p>
              )}
            </div>

            {/* Add More Section */}
            <div className="flex-1 flex flex-col min-h-0 bg-background">
              <div className="p-4 border-b border-border/60 bg-background z-10">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground/60" />
                  <Input
                    placeholder="Start typing a habit here"
                    className="pl-9 bg-muted/30 border-border/50 rounded-xl z-body placeholder:text-muted-foreground/50"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
              </div>

              <div className="flex-1 overflow-y-auto p-6">
                <h3 className="z-label text-muted-foreground mb-3">
                  Add more from below
                </h3>
                <div className="grid grid-cols-3 gap-2.5">
                  {availableTypes.map((type) => (
                    <button
                      key={type.id}
                      onClick={() => handleAddClick(type.id)}
                      className="flex flex-col items-start p-3 rounded-xl border border-border bg-card hover:border-primary/40 hover:shadow-sm transition-all text-left relative group"
                    >
                      <div className="mb-2 p-1.5 bg-primary/10 text-primary rounded-lg">
                        <Coffee className="h-4 w-4" />
                      </div>
                      <span className="z-body-md text-foreground line-clamp-2">
                        {type.name}
                      </span>

                      <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
                        <div className="h-5 w-5 bg-primary rounded-full flex items-center justify-center shadow-sm text-primary-foreground">
                          <Plus className="h-3 w-3" />
                        </div>
                      </div>
                    </button>
                  ))}
                  {availableTypes.length === 0 && (
                    <div className="col-span-3 text-center py-8 text-muted-foreground z-body">
                      No matching habits found
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="flex-1 overflow-y-auto p-6">
            <Form {...form}>
              <form
                onSubmit={form.handleSubmit(onSubmit)}
                className="space-y-6"
              >
                {!isEditing && (
                  <div className="p-4 bg-primary/5 rounded-xl border border-primary/20 mb-6">
                    <h3 className="z-body-md text-foreground">
                      {selectedType
                        ? getTypeName(selectedType)
                        : "New Lifestyle"}
                    </h3>
                  </div>
                )}

                <FormField
                  control={form.control}
                  name="lifestyle_frequency"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Frequency</FormLabel>
                      <Select
                        onValueChange={(value) =>
                          field.onChange(parseInt(value))
                        }
                        value={field.value ? field.value.toString() : ""}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select from dropdown" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {lifestyleFrequencies.map((freq) => (
                            <SelectItem
                              key={freq.id}
                              value={freq.id.toString()}
                            >
                              {freq.frequency}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div className="flex justify-end pt-4">
                  <Button
                    type="submit"
                    disabled={isLoading}
                    className="w-full sm:w-auto"
                  >
                    {isLoading
                      ? "Saving..."
                      : isEditing
                        ? "Update Lifestyle"
                        : "Add Lifestyle"}
                  </Button>
                </div>
              </form>
            </Form>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
